import { useCallback, useEffect, useMemo, useState } from "react";
import {
  askedThisSession,
  claimed,
  deferred,
  type InstallMemory,
  markAskedThisSession,
  nudgeDue,
  PERSISTENT_AFTER,
  readMemory,
  stepDue,
  writeMemory,
} from "./nudge";
import { detectPlatform, type Guide, installGuide, isStandalone } from "./platform";
import { type InstallOutcome, installPrompt } from "./prompt";

export interface InstallApi {
  /** What the instructions should say, already resolved for this browser. */
  guide: Guide;
  /** Running installed right now — from the home screen, dock or launcher. */
  installed: boolean;
  /** The full-screen onboarding step should take over. */
  stepDue: boolean;
  /** The slim banner on the larder should show. */
  nudgeDue: boolean;
  /** They have declined enough times that the copy should stop selling. */
  persistent: boolean;
  /** Fires the native dialog when there is one; "unavailable" when there isn't. */
  install: () => Promise<InstallOutcome>;
  /** "Not now" — recorded, and the banner takes over for the rest of the session. */
  later: () => void;
  /** "I've done it", or the browser confirmed it. */
  finish: () => void;
  /** Re-opens the step from Settings, at any time. */
  reopen: () => void;
}

/**
 * Everything the install nudge needs, in one hook.
 *
 * The three inputs it reconciles are genuinely independent: what the BROWSER is
 * (a pure read of the UA), what the USER has already said (storage), and
 * whether Chromium has handed over a real prompt (an event that may arrive at
 * any moment, or never). Only the last two change during a session.
 *
 * @param uid the signed-in user, so a second person on a shared phone is asked
 *   in their own right rather than inheriting the first one's "not now".
 */
export function useInstall(uid: string | null): InstallApi {
  const [memory, setMemory] = useState<InstallMemory>(() => readMemory());
  const [promptReady, setPromptReady] = useState(() => installPrompt.ready());
  const [installed, setInstalled] = useState(
    () => isStandalone() || installPrompt.justInstalled(),
  );
  /**
   * Mirrors sessionStorage in React state so that closing the step re-renders.
   * Seeded from storage so a RELOAD mid-session does not ask a second time —
   * the ask is per app-open, and a refresh is the same opening.
   */
  const [asked, setAsked] = useState(() => (uid ? askedThisSession(uid) : false));

  // Signing in as someone else in the same tab is a new ask.
  useEffect(() => {
    setAsked(uid ? askedThisSession(uid) : false);
  }, [uid]);

  const platform = useMemo(
    () =>
      detectPlatform(
        typeof navigator === "undefined" ? "" : navigator.userAgent,
        typeof navigator === "undefined" ? 0 : navigator.maxTouchPoints,
      ),
    [],
  );

  // The event can land long after first paint — Chromium waits for its own
  // installability checks — so the button appears when it does.
  useEffect(
    () =>
      installPrompt.subscribe(() => {
        setPromptReady(installPrompt.ready());
        if (installPrompt.justInstalled()) setInstalled(true);
      }),
    [],
  );

  /**
   * Someone can install the app while this tab is open and then switch back to
   * it — on desktop that is the normal shape of an install, since the new
   * window opens alongside rather than replacing. Watching `display-mode` stops
   * the banner nagging a user who has already done what it asked.
   */
  useEffect(() => {
    const mq = window.matchMedia("(display-mode: standalone)");
    const sync = () => setInstalled(isStandalone() || installPrompt.justInstalled());
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const remember = useCallback((next: InstallMemory) => {
    setMemory(next);
    writeMemory(next);
  }, []);

  /** Closes the step for this session, whatever the reason. */
  const closeStep = useCallback(() => {
    if (uid) markAskedThisSession(uid);
    setAsked(true);
  }, [uid]);

  const install = useCallback(async (): Promise<InstallOutcome> => {
    const outcome = await installPrompt.show();
    setPromptReady(installPrompt.ready());
    if (outcome === "accepted") {
      // `appinstalled` also fires and flips `installed`; recording it here too
      // means the state is right even if that event is missed.
      remember(claimed(memory, Date.now()));
      setInstalled(true);
    }
    if (outcome === "dismissed") remember(deferred(memory, Date.now()));
    closeStep();
    return outcome;
  }, [memory, remember, closeStep]);

  const later = useCallback(() => {
    remember(deferred(memory, Date.now()));
    closeStep();
  }, [memory, remember, closeStep]);

  const finish = useCallback(() => {
    remember(claimed(memory, Date.now()));
    closeStep();
  }, [memory, remember, closeStep]);

  const reopen = useCallback(() => setAsked(false), []);

  const guide = useMemo(
    () => installGuide(platform, promptReady),
    [platform, promptReady],
  );

  return {
    guide,
    installed,
    // Nobody sees the step until they are signed in — it is an onboarding step,
    // and the landing page has its own job.
    stepDue: uid !== null && stepDue(installed, asked),
    nudgeDue: uid !== null && nudgeDue(installed, asked),
    persistent: memory.dismissals >= PERSISTENT_AFTER,
    install,
    later,
    finish,
    reopen,
  };
}
